import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Container, Box, CircularProgress, Paper, Typography, Button } from '@mui/material';
import { NatalToTransitCalendar } from '../components/NatalToTransitCalendar';
import TransitCalendarLegend from '../components/TransitCalendarLegend';
import { getNatalData } from '../services/transitCalendarService';
import { resolveBirthMoment } from '../utils/natalDate';
import { getCalendarAccess } from '../utils/calendarAccess';

const NatalToTransitCalendarPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const profileId = searchParams.get('profile_id');

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [natalData, setNatalData] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Filter states
  const [houseSystem, setHouseSystem] = useState('whole-sign'); // Default to Whole Sign
  const [firstHouseReference, setFirstHouseReference] = useState('ascendant');
  const [manualFirstHouseSign, setManualFirstHouseSign] = useState('Aries');
  const [showDisplayOptions, setShowDisplayOptions] = useState(false);

  useEffect(() => {
    const loadProfileData = async () => {
      // Check authentication
      const token = localStorage.getItem('transit-calendar-token');
      if (!token) {
        navigate('/transit-calendar/login');
        return;
      }

      // If no profile_id, redirect to profile selection
      if (!profileId) {
        navigate('/transit-calendar/profiles');
        return;
      }

      // Fetch specific profile data from API
      try {
        const data = await getNatalData(profileId);
        setProfileData(data);
        setNatalData(data);  // Keep for backwards compatibility
        setIsAuthenticated(true);
      } catch (err) {
        console.error('Error loading profile data from API:', err);
        // If fetch fails, redirect to profile selection
        navigate('/transit-calendar/profiles');
      }

      setLoading(false);
    };

    loadProfileData();
  }, [navigate, profileId]);

  const handleLogout = () => {
    localStorage.removeItem('transit-calendar-token');
    localStorage.removeItem('transit-calendar-access-token');
    localStorage.removeItem('transit-calendar-refresh-token');
    localStorage.removeItem('transit-calendar-user');
    localStorage.removeItem('transit-calendar-email');
    navigate('/transit-calendar/login');
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
          Loading your Personal Cosmic Activation...
        </Typography>
      </Container>
    );
  }

  if (!isAuthenticated || !natalData) {
    return null; // Will redirect via useEffect
  }

  // Permissions from DynamoDB: weekly is free, monthly and annual are paid (annual calendars are sold per year)
  const nttAccess = getCalendarAccess(natalData.permissions, 'ntt');
  const nttWeekly = nttAccess.weekly;
  const nttMonthly = nttAccess.monthly;
  const nttAnnual = nttAccess.annual;

  const showNav = true; // Always show navigation
  const allowedYears = [2026, 2027, 2028]; // All years accessible
  const monthlyAccess = false; // Not used

  // Birth moment resolved device-independently (fixes natal chart shifting
  // when the site is opened from a different timezone than registration)
  const birthMoment = resolveBirthMoment(natalData);
  const natalBirthDate = birthMoment ? birthMoment.localDate : new Date(natalData.birth_date);

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header with user info and logout */}
      <Box sx={{
        mb: 3,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', sm: 'center' },
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 2
      }}>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' }
            }}
          >
            {natalData.profile_name || natalData.name}'s Personal Cosmic Activation
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
          >
            {natalData.birth_location} • {natalBirthDate.toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </Typography>
        </Box>
        <Box sx={{
          display: 'flex',
          gap: 1,
          width: { xs: '100%', sm: 'auto' },
          flexDirection: { xs: 'row', sm: 'row' }
        }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/transit-calendar/profiles')}
            sx={{
              borderColor: '#667eea',
              color: '#667eea',
              flex: { xs: 1, sm: 'none' },
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              px: { xs: 1, sm: 2 },
              '&:hover': { borderColor: '#764ba2', bgcolor: 'rgba(102, 126, 234, 0.04)' }
            }}
          >
            All Profiles
          </Button>
          <Button
            variant="outlined"
            onClick={handleLogout}
            sx={{
              borderColor: '#667eea',
              color: '#667eea',
              flex: { xs: 1, sm: 'none' },
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              px: { xs: 1, sm: 2 },
              '&:hover': { borderColor: '#764ba2', bgcolor: 'rgba(102, 126, 234, 0.04)' }
            }}
          >
            Log Out
          </Button>
        </Box>
      </Box>

      {/* How to Read Guide */}
      <TransitCalendarLegend calendarType="personal-cosmic-activation" />

      {/* Free Tier Notice - Only show for users without annual access */}
      {!nttAnnual && (
        <Paper sx={{
          p: { xs: 2, sm: 3 },
          mb: 3,
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
          border: '2px solid rgba(102, 126, 234, 0.3)'
        }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              mb: 1,
              fontSize: { xs: '1rem', sm: '1.25rem' },
              color: '#fff'
            }}
          >
            {nttWeekly && !nttMonthly ? '🔓 Free Access: Current Week + Next Week' : '📅 Limited Access'}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              mb: 2,
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              color: 'rgba(255,255,255,0.8)'
            }}
          >
            {nttWeekly && !nttMonthly
              ? "You have free access to view the current week and next week. Upgrade to unlock monthly or full year calendar navigation."
              : nttMonthly
              ? "You have monthly access. Upgrade to annual for full year navigation."
              : "You're viewing limited calendar access. Upgrade to unlock full navigation."}
          </Typography>
          <Button
            variant="contained"
            size="small"
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              fontSize: { xs: '0.75rem', sm: '0.8125rem' },
              py: { xs: 0.75, sm: 1 },
              px: { xs: 1.5, sm: 2 },
              '&:hover': { background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)' }
            }}
            onClick={() => navigate('/pricing?section=ntt-calendar')}
          >
            Upgrade for Full Access
          </Button>
        </Paper>
      )}

      {/* Filter Controls */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Box
          onClick={() => setShowDisplayOptions(!showDisplayOptions)}
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none' }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.125rem' } }}
          >
            Display Options
          </Typography>
          <Typography sx={{ fontSize: '1rem', color: '#666', transition: 'transform 0.2s', transform: showDisplayOptions ? 'rotate(180deg)' : 'rotate(0deg)' }}>
            ▼
          </Typography>
        </Box>
        {showDisplayOptions && (
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 2,
            mt: 2
          }}>
            {/* House System Selector */}
            <Box>
              <Typography
                variant="body2"
                sx={{ mb: 1, fontWeight: 500, fontSize: { xs: '0.875rem', sm: '0.9375rem' } }}
              >
                House System
              </Typography>
              <select
                value={houseSystem}
                onChange={(e) => setHouseSystem(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  fontSize: '14px',
                  borderRadius: '4px',
                  border: '1px solid rgba(201,168,124,0.2)',
                  backgroundColor: '#1a1a2e',
                  color: '#ede8e0',
                  cursor: 'pointer'
                }}
              >
                <option value="whole-sign">Whole Sign (Default)</option>
                <option value="placidus">Placidus</option>
              </select>
            </Box>

            {/* First House Reference Selector */}
            <Box>
              <Typography
                variant="body2"
                sx={{ mb: 1, fontWeight: 500, fontSize: { xs: '0.875rem', sm: '0.9375rem' } }}
              >
                First House Reference
              </Typography>
              <select
                value={firstHouseReference}
                onChange={(e) => setFirstHouseReference(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  fontSize: '14px',
                  borderRadius: '4px',
                  border: '1px solid rgba(201,168,124,0.2)',
                  backgroundColor: '#1a1a2e',
                  color: '#ede8e0',
                  cursor: 'pointer'
                }}
              >
                <option value="ascendant">Ascendant (Calculated)</option>
                <option value="sun">Sun Sign</option>
                <option value="moon">Moon Sign</option>
              </select>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Natal to Transit-Transit Calendar */}
      <NatalToTransitCalendar
        natalDate={natalBirthDate}
        natalDateUTC={birthMoment ? birthMoment.utc : undefined}
        natalLatitude={natalData.birth_lat}
        natalLongitude={natalData.birth_lon}
        natalTimezone={natalData.timezone}
        firstHouseReference={firstHouseReference}
        manualFirstHouseSign={manualFirstHouseSign}
        cityName={natalData.birth_location}
        houseSystem={houseSystem}
        showWeekNavigation={showNav}
        currentWeekOnly={!showNav}
        allowedYears={allowedYears}
        monthlyAccess={monthlyAccess}
        nttWeekly={nttWeekly}
        nttMonthly={nttMonthly}
        nttAnnual={nttAnnual}
        nttWeeklyStartDate={nttAccess.weeklyStartDate}
        nttMonthlyStartDate={nttAccess.monthlyStartDate}
        nttAnnualStartDate={nttAccess.annualStartDate}
        nttAnnualEndDate={nttAccess.annualEndDate}
      />

      {/* Upgrade Prompt at Bottom - Only show for users without annual access */}
      {!nttAnnual && (
        <Paper sx={{ p: { xs: 3, sm: 4 }, mt: 4, textAlign: 'center', bgcolor: '#1a1a2e', border: '1px solid rgba(201,168,124,0.12)' }}>
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
              color: '#ede8e0'
            }}
          >
            Unlock Full Calendar Access
          </Typography>
          <Typography
            variant="body1"
            paragraph
            sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, color: '#8a8a9a' }}
          >
            {nttWeekly && !nttMonthly
              ? "Upgrade to unlock monthly or full year calendar navigation, view past transits, and plan ahead with extended calendar access."
              : nttMonthly
              ? "Upgrade to annual access for full year navigation and never miss an important transit."
              : "Get full access to navigate through months and years of personalized natal-to-current transits."}
          </Typography>
          <Button
            variant="contained"
            size="large"
            sx={{
              py: { xs: 1.25, sm: 1.5 },
              px: { xs: 3, sm: 4 },
              fontSize: { xs: '0.875rem', sm: '1rem' },
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': { background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)' }
            }}
            onClick={() => navigate('/pricing?section=ntt-calendar')}
          >
            View Pricing Options
          </Button>
        </Paper>
      )}
    </Container>
  );
};

export default NatalToTransitCalendarPage;
